import { format, isValid, parse, parseISO } from 'date-fns'
import { CalendarIcon } from 'lucide-react'
import * as React from 'react'

import type { CalendarProps } from '@/components/ui/calendar'

import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

export interface CustomDatePickerProps
  extends Omit<CalendarProps, 'mode' | 'selected' | 'onSelect' | 'disabled' | 'className'> {
  value?: string
  onChange?: (value: string) => void
  placeholder?: string
  disabled?: boolean
  disableFuture?: boolean
  disablePast?: boolean
  minDate?: Date
  maxDate?: Date
  className?: string
}

function parseDate(value?: string): Date | undefined {
  if (!value)
    return undefined

  const trimmed = value.trim()
  if (!trimmed)
    return undefined

  // Handle plain ISO date strings (yyyy-MM-dd) in the local timezone
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const parsed = parse(trimmed, 'yyyy-MM-dd', new Date())
    if (isValid(parsed)) {
      return parsed
    }
  }

  try {
    const parsedIso = parseISO(trimmed)
    if (isValid(parsedIso)) {
      return parsedIso
    }
  }
  catch {
    // ignore parseISO errors and fall back to native parsing
  }

  const nativeParsed = new Date(trimmed)
  return isValid(nativeParsed) ? nativeParsed : undefined
}

function getDefaultMinDate(): Date {
  const date = new Date()
  date.setFullYear(date.getFullYear() - 100)
  return date
}

export function DatePicker({
  value,
  onChange,
  placeholder = 'Pick a date',
  disabled = false,
  disableFuture = false,
  disablePast = false,
  minDate,
  maxDate,
  className,
  ...props
}: CustomDatePickerProps) {
  const selectedDate = parseDate(value)
  const resolvedMinDate = minDate ?? getDefaultMinDate()

  const getDisabledDates = React.useCallback(
    (date: Date) => {
      const today = new Date()
      today.setHours(0, 0, 0, 0)

      if (disableFuture && date > today)
        return true
      if (disablePast && date < today)
        return true
      if (resolvedMinDate && date < resolvedMinDate)
        return true
      if (maxDate && date > maxDate)
        return true

      return false
    },
    [disableFuture, disablePast, resolvedMinDate, maxDate],
  )

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={cn(
            'w-full justify-start text-left font-normal',
            !selectedDate && 'text-muted-foreground',
            className,
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {selectedDate ? format(selectedDate, 'PPP') : <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={(date: Date | undefined) => onChange?.(date ? format(date, 'yyyy-MM-dd') : '')}
          disabled={getDisabledDates}
          captionLayout="dropdown"
          startMonth={disablePast ? new Date(new Date().getFullYear(), 0) : new Date(new Date().getFullYear() - 100, 0)}
          endMonth={disableFuture ? new Date(new Date().getFullYear(), 11) : new Date(new Date().getFullYear() + 100, 11)}
          {...props}
        />
      </PopoverContent>
    </Popover>
  )
}

// Legacy component for backward compatibility
interface DatePickerDemoProps {
  date?: Date | null
  onDateChange?: (date: Date) => void
  placeholder?: string
  minDate?: Date
  maxDate?: Date
}

export function DatePickerDemo({
  date,
  onDateChange,
  placeholder = 'Pick a date',
  minDate,
  maxDate,
}: DatePickerDemoProps) {
  const value = date ? format(date, 'yyyy-MM-dd') : ''

  return (
    <DatePicker
      value={value}
      onChange={(newValue) => {
        const parsed = parseDate(newValue)
        if (parsed && onDateChange) {
          onDateChange(parsed)
        }
      }}
      placeholder={placeholder}
      minDate={minDate}
      maxDate={maxDate}
      className="w-[180px] border-none"
    />
  )
}
